/**
 * frontend/src/modules/phenomena/lib/api.ts
 * Appels API — Phénomènes Marché P1 (V4.20).
 *
 * Endpoints :
 *   GET /api/v1/phenomena/active             — RBAC market.view
 *   GET /api/v1/phenomena/:id/drill           — RBAC market.view
 */

import { apiClient } from '@/lib/apiClient'
import type {
  ActivePhenomenaResponse,
  DrillResponse,
} from '../types/phenomena.types'

export interface ActivePhenomenaParams {
  week_iso?:     string
  severity?:     string
  kpi?:          string
  pattern_type?: string
  /** Filtre canal — #338 D-PHENOMENA-OVERVIEW-CANAL-CABLAGE (21/07). Absent = pas de
   *  filtre (défaut historique). 'retail'/'wholesale' = strict, 'all' = phénomènes
   *  canal-agnostiques (OPS K15-K17). */
  channel?:      'retail' | 'wholesale' | 'all'
}

export async function fetchActivePhenomena(
  params?: ActivePhenomenaParams,
): Promise<ActivePhenomenaResponse> {
  const query = new URLSearchParams()
  if (params?.week_iso)     query.set('week_iso',     params.week_iso)
  if (params?.severity)     query.set('severity',     params.severity)
  if (params?.kpi)          query.set('kpi',          params.kpi)
  if (params?.pattern_type) query.set('pattern_type', params.pattern_type)
  if (params?.channel)      query.set('channel',      params.channel)
  const qs = query.toString()
  const res = await apiClient.get<ActivePhenomenaResponse>(
    `/phenomena/active${qs ? `?${qs}` : ''}`,
  )
  return res.data
}

// ── V4.20 B2 — Liste semaines (nature-aware) ───────────────────────────────

export type PhenomenaNature = 'MARCHE' | 'OPS'

export async function fetchPhenomenaWeeks(
  nature?: PhenomenaNature,
): Promise<{ data: string[] }> {
  const qs = nature ? `?nature=${nature}` : ''
  const res = await apiClient.get<{ data: string[] }>(`/phenomena/weeks${qs}`)
  return res.data
}

// ── D-PHENOMENA-UI-3-ETATS (#315) ────────────────────────────────────────────

export interface PhenomenaRun {
  weekIso:         string
  status:          string
  detectedCount:   number | null
  patternsDormant: string[]
  finishedAt:      string | null
  errorMessage:    string | null
  /**
   * `P2` (`#573`) — QUATRIÈME ÉTAT : détection PÉRIMÉE (un import est postérieur
   * au dernier run de cette semaine).
   * ⚠ `null` = on ne peut pas conclure (pas « à jour »).
   */
  stale:           boolean | null
  lastImportAt:    string | null
}

/** `data: null` = aucun run trouve (etat 2 — "en attente d'analyse"). */
export async function fetchLatestPhenomenaRun(weekIso?: string): Promise<{ data: PhenomenaRun | null }> {
  const qs = weekIso ? `?week_iso=${encodeURIComponent(weekIso)}` : ''
  const res = await apiClient.get<{ data: PhenomenaRun | null }>(`/phenomena/runs/latest${qs}`)
  return res.data
}

export interface DrillParams {
  dimension:     'region' | 'wilaya' | 'pdv' | 'brand'
  context_key?:  'region_id' | 'wilaya_id'
  context_value?: string
  includeSouth?: boolean
  topN?:         number
}

export async function fetchPhenomenonDrill(
  id: string,
  params: DrillParams,
): Promise<DrillResponse> {
  const sp = new URLSearchParams()
  sp.set('dimension', params.dimension)
  if (params.context_key)   sp.set('context_key',   params.context_key)
  if (params.context_value) sp.set('context_value', params.context_value)
  if (params.includeSouth !== undefined) sp.set('includeSouth', String(params.includeSouth))
  if (params.topN !== undefined)         sp.set('topN',         String(params.topN))
  const res = await apiClient.get<DrillResponse>(`/phenomena/${id}/drill?${sp.toString()}`)
  return res.data
}

// ── Relance de la détection (#528 P0) ────────────────────────────────────────

export interface RedetectResponse {
  weeks:    string[]
  enqueued: number
  skipped:  string[]
  message:  string
}

/**
 * Relance la détection des phénomènes sur une plage de semaines.
 * `from`/`to` absents = tout l'historique présent.
 *
 * ⚠ `undefined` et NON `null` comme corps absent n'est pas le sujet ici : on
 *   envoie un vrai objet JSON, axios pose donc `application/json` de lui-même
 *   (le piège du 415 mesuré au `#522` ne concerne que les POST SANS corps).
 */
export async function redetectPhenomena(
  from?: string | null,
  to?:   string | null,
): Promise<RedetectResponse> {
  const res = await apiClient.post<RedetectResponse>('/phenomena/redetect', {
    from: from ?? null,
    to:   to   ?? null,
  })
  return res.data
}
